import { useState } from "react";
import { TrendingUp, TrendingDown, Eye, EyeOff, ChevronDown, RefreshCcw, Download } from "lucide-react";

// Native-app-style hero header — big gradient card with portfolio value, P/L,
// quick metrics. Shown ONLY on mobile at the top of the Portfolio page.
// Inspired by neobanking apps (CRED, Jupiter) — focus on one big number.
const MobilePortfolioHero = ({
  selectedClientObj,
  marketValue = 0,
  costValue = 0,
  totalProfitLoss = 0,
  xirr = null,
  onClientClick,
  onRefresh,
  onExport,
  exportDisabled = false,
  loading = false,
}) => {
  const [hidden, setHidden] = useState(false);

  const fmtCurrency = (v) => {
    const abs = Math.abs(v || 0);
    if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    return `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
  };
  const fmtFull = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
  const plPercent = costValue > 0 ? ((totalProfitLoss / costValue) * 100).toFixed(2) : "0.00";
  const isPositive = (totalProfitLoss || 0) >= 0;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const firstName = (selectedClientObj?.name || "").split(" ")[0] || "";

  return (
    <div
      className="relative overflow-hidden rounded-2xl text-white px-5 pt-5 pb-6 shadow-lg"
      style={{
        background: "linear-gradient(135deg, #1a3f3a 0%, #2d6b62 60%, #3a8a7e 100%)",
      }}
      data-testid="mobile-portfolio-hero"
    >
      {/* decorative blobs */}
      <div className="absolute -top-12 -right-10 w-40 h-40 rounded-full opacity-20" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-16 -left-8 w-44 h-44 rounded-full opacity-15" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)" }} />

      <div className="relative">
        {/* Greeting + client switcher */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="min-w-0">
            <p className="text-xs text-white/70">{greeting}</p>
            <button
              onClick={onClientClick}
              className="flex items-center gap-1 max-w-full text-left active:opacity-70"
              data-testid="hero-client-switcher"
            >
              <span className="font-semibold text-base truncate">{firstName || selectedClientObj?.name || "Select client"}</span>
              {onClientClick && <ChevronDown className="w-4 h-4 flex-shrink-0 opacity-80" />}
            </button>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white/90 active:bg-white/25 disabled:opacity-40"
                data-testid="hero-refresh-btn"
                aria-label="Refresh"
              >
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            )}
            {onExport && (
              <button
                onClick={onExport}
                disabled={exportDisabled}
                className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white/90 active:bg-white/25 disabled:opacity-40"
                data-testid="hero-export-btn"
                aria-label="Export"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setHidden((v) => !v)}
              className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-white/90 active:bg-white/25"
              data-testid="hero-hide-toggle"
              aria-label={hidden ? "Show values" : "Hide values"}
            >
              {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Big portfolio value */}
        <div className="mt-1">
          <p className="text-[11px] uppercase tracking-wider text-white/70 font-medium">Portfolio value</p>
          <p className="text-[32px] font-bold tracking-tight leading-tight mt-0.5" data-testid="hero-market-value">
            {loading ? "—" : hidden ? "₹ ••••••" : fmtFull(marketValue)}
          </p>
        </div>

        {/* P/L pill */}
        <div className="flex items-center gap-2 mt-2">
          <div
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
              isPositive ? "bg-green-500/25 text-green-100" : "bg-red-500/30 text-red-100"
            }`}
          >
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{hidden ? "•••" : (isPositive ? "+" : "") + fmtCurrency(totalProfitLoss)}</span>
            <span className="opacity-80">({hidden ? "•••" : (isPositive ? "+" : "") + plPercent + "%"})</span>
          </div>
        </div>

        {/* Sub-metrics row */}
        <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/15">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-medium">Invested</p>
            <p className="text-sm font-semibold mt-0.5">{loading ? "—" : hidden ? "•••" : fmtCurrency(costValue)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-medium">XIRR</p>
            <p className="text-sm font-semibold mt-0.5">
              {loading
                ? "—"
                : xirr == null || isNaN(xirr)
                ? "—"
                : `${(xirr * 100).toFixed(2)}%`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilePortfolioHero;
